import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import { BarChart } from '@mui/x-charts/BarChart';
import Tooltip from '@mui/material/Tooltip';

import { containerStyles, convertSecToMinAndSec } from './Dashboard'
import Loading from '../Loading';

export const analyticsBoxStyles = {
    width: {xs: '100%', md: '30%'}, 
    borderRadius: 2, 
    marginTop: {xs: 0, sm: 5}, 
    padding: 3, 
    backgroundColor: '#f5f5f5',
    mb: { xs: 2, sm: 0 },
}

export const iconStyles = {
    verticalAlign: 'middle',
    marginRight: 1
}

export const metricsBoxStyles = (theme) => ({
    display: 'flex', 
    flexDirection: 'column', 
    alignItems: 'center',
    borderRadius: 2,
    padding: 2,
    ':hover': {
        border: 2,
        borderColor: theme.palette.primary.main,
    }
})

export const metricsContainerStyles = {
    display: 'flex', 
    justifyContent: 'space-around', 
    marginTop: {xs: 0, sm: 2},
}

const VideoTabPanel = ({ children, value, index, data, viewsChartData, completionChartData, watchTimeChartData, ...other }) => {
    // Bar chart data for Views Analytics
    const viewsSeries = [];
    viewsSeries.push({data: viewsChartData.map(item => item.totalViews), color: '#4facfe', label: 'Total Views'});
    viewsSeries.push({data: viewsChartData.map(item => item.uniqueViews), color: '#764ba2', label: 'Unique Views'});

    // Line chart data
    const minFormatter = (v) => (v ? `${v} min` : '-');
    const videoTitles = viewsChartData ? viewsChartData.map(item => item.title) : [];
    const watchTimeSeries = [];
    watchTimeSeries.push({data: watchTimeChartData.map(item =>  Math.ceil(item.total / 60) ), label: 'Watch Time (minutes)', valueFormatter: minFormatter, area: true, color: '#667eea' });
    watchTimeSeries.push({data: watchTimeChartData.map(item =>  Math.ceil(item.avg / 60)), label: 'Average Watch Time (minutes)', valueFormatter: minFormatter, area: true, color: '#ffc107' });

    // Bar chart data for Completion Rates
    const completionRatesSeries = [];
    const percentageFormatter = (v) => (v ? `${v} %` : '-');
    completionRatesSeries.push({ data: completionChartData.map(item => Math.floor(item.byPlays * 100)), color: '#4facfe', label: 'Based on Plays', valueFormatter: percentageFormatter });
    completionRatesSeries.push({ data: completionChartData.map(item => Math.floor(item.byViewers * 100)), color: '#ed4e5d', label: 'Based on Viewers', valueFormatter: percentageFormatter  });
    completionRatesSeries.push({ data: completionChartData.map(item => Math.floor(item.byAssignedUsers * 100)), color: '#ffc107', label: 'Based on Assigned Users', valueFormatter: percentageFormatter  });

    const percentage = (num) => {
        if (num === undefined || num === null) return 'N/A';
        return `${Math.floor(num * 100)}%`;
    }

    if (!data.totalViews || !data.avgWatchTime || !data.totalWatchTime) {
        return <Loading />;
    }

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`video-tabpanel-${index}`}
            aria-labelledby={`video-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ ...containerStyles, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between' }}>
                    {data ? (
                        <>
                            {/* Views Analytics */}
                            <Box sx={analyticsBoxStyles}>
                                <Typography variant='h6' sx={{ fontWeight: '600'}}><VisibilityIcon sx={iconStyles}/>Views Analytics {data.title ? `for ${data.title}` : ''}</Typography>
                                <Box sx={metricsContainerStyles}>
                                    <Tooltip title="Total number of times the video has been viewed, including multiple views by the same user." arrow>
                                        <Box sx={metricsBoxStyles}>
                                            <Typography variant='h5' sx={{ fontWeight: '600' }}>{data.totalViews}</Typography>
                                            <Typography variant='body1'>Total Views</Typography>
                                        </Box>
                                    </Tooltip>
                                    <Tooltip title="Number of unique users who have viewed the video." arrow>
                                        <Box sx={metricsBoxStyles}>
                                            <Typography variant='h5' sx={{ fontWeight: '600' }}>{data.uniqueViews}</Typography>
                                            <Typography variant='body1'>Unique Views</Typography>
                                        </Box>
                                    </Tooltip>
                                </Box>
                                <BarChart
                                    xAxis={[{
                                        data: viewsChartData.map(item => item.title),
                                        valueFormatter: (v, context) =>
                                            context.location === 'tick' ? v.slice(0, 12) : v
                                    }]}
                                    yAxis={[{ label: 'Number of Views', width: 45 }]}
                                    series={viewsSeries}
                                    height={400}
                                    borderRadius={6}
                                />
                            </Box>
                            {/* Watch Time Analytics */}
                            <Box sx={analyticsBoxStyles}>
                                <Typography variant='h6' sx={{ fontWeight: '600'}}><WatchLaterIcon sx={iconStyles} />Watch Time Analytics</Typography>
                                <Box sx={metricsContainerStyles}>
                                    <Tooltip title="Average watch time per view across all users." arrow>
                                        <Box sx={metricsBoxStyles}>
                                            <Typography variant='h5' sx={{ fontWeight: '600' }}>{convertSecToMinAndSec(data.avgWatchTime)}</Typography>
                                            <Typography variant='body1' sx={{ textAlign: 'center' }}>Average Watch Time</Typography>
                                        </Box>
                                    </Tooltip>
                                    <Tooltip title="Total watch time across all users." arrow>
                                        <Box sx={metricsBoxStyles}>
                                            <Typography variant='h5' sx={{ fontWeight: '600' }}>{convertSecToMinAndSec(data.totalWatchTime)}</Typography>
                                            <Typography variant='body1'>Total Time</Typography>
                                        </Box>
                                    </Tooltip>
                                </Box>
                                <BarChart
                                    series={watchTimeSeries}
                                    xAxis={[{ 
                                        data: videoTitles,
                                        valueFormatter: (v, context) =>
                                            context.location === 'tick' ? v.slice(0, 12) : v
                                    }]}
                                    yAxis={[{ label: 'Time (minutes)', width: 45 }]}
                                    height={400}
                                    borderRadius={6}
                                />    
                            </Box>
                            {/* Completion Rates */}
                            <Box sx={analyticsBoxStyles}>
                                <Typography variant='h6' sx={{ fontWeight: '600'}}><CheckBoxIcon sx={iconStyles} />Completion Rates</Typography>
                                <Box sx={metricsContainerStyles}>
                                    <Tooltip title="Percentage of playbacks that reach completion." arrow>
                                        <Box sx={metricsBoxStyles}>
                                            <Typography variant='h5' sx={{ fontWeight: '600' }}>{percentage(data.playsCompletionRate)}</Typography>
                                            <Typography variant='body1' sx={{ textAlign: 'center' }}>Based on Plays</Typography>
                                        </Box>
                                    </Tooltip>
                                    <Tooltip title="Percentage of users who watch the video to completion out of the total number of users who start watching the video." arrow>
                                        <Box sx={metricsBoxStyles}>
                                            <Typography variant='h5' sx={{ fontWeight: '600' }}>{percentage(data.usersCompletionRate.viewers)}</Typography>
                                            <Typography variant='body1' sx={{ textAlign: 'center' }}>Based on Viewers</Typography>
                                        </Box>
                                    </Tooltip>
                                    {data.usersCompletionRate.assigned_users && (
                                    <Tooltip title="Percentage of users who watch the video to completion out of the total number of users who were assigned the video." arrow>
                                        <Box sx={metricsBoxStyles}>
                                            <Typography variant='h5' sx={{ fontWeight: '600' }}>{percentage(data.usersCompletionRate.assigned_users)}</Typography>
                                            <Typography variant='body1' sx={{ textAlign: 'center' }}>Based on Assigned Users</Typography>
                                        </Box>
                                    </Tooltip>
                                    )}
                                </Box>
                                  <BarChart
                                    xAxis={[{
                                        data: completionChartData.map(item => item.title), 
                                        valueFormatter: (v, context) =>
                                            context.location === 'tick' ? v.slice(0, 12) : v
                                    }]}
                                    yAxis={[{ label: 'Completion Rate (%)', width: 45 }]}
                                    series={completionRatesSeries}
                                    height={400}
                                    borderRadius={6}
                                    tooltip={{ trigger: 'item'}}
                                />
                            </Box>
                        </>
                    ) : (
                        {children}
                        )
                    }
                </Box>
            )}
        </div>
    )
}

export default VideoTabPanel;